/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import {
    DateFormats,
    formatDate,
    formatNameLastFirst,
    sortAsc,
    sortDateAsc,
    useDebouncedFunction,
} from '@strata/helper-functions';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, PrimaryData, ProductCell, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { AddToBatchDialog } from 'components/upcomingTreatments/dialogs/AddToBatchDialog';
import { CreateBatchDialog } from 'components/upcomingTreatments/dialogs/CreateBatchDialog';
import { PreferredPharmacyOfferDialog } from 'components/upcomingTreatments/dialogs/PreferredPharmacyOfferDialog';
import { ReleaseBatchDialog } from 'components/upcomingTreatments/dialogs/ReleaseBatchDialog';
import { RemoveFromBatchDialog } from 'components/upcomingTreatments/dialogs/RemoveFromBatchDialog';
import { RemovePreferredPharmacyOfferDialog } from 'components/upcomingTreatments/dialogs/RemovePreferredPharmacyOfferDialog';
import { ReprocessTreatmentDialog } from 'components/upcomingTreatments/dialogs/ReprocessTreatmentDialog';
import { RunRouteSimulatorDialog } from 'components/upcomingTreatments/dialogs/RunRouteSimulatorDialog';
import {
    buildGroupFilterCategory,
    groupIdFromActiveFilters,
} from 'components/upcomingTreatments/groupFilter';
import { PreferredPharmacyOfferCell } from 'components/upcomingTreatments/PreferredPharmacyOfferCell';
import { ReleaseStatusIndicator } from 'components/upcomingTreatments/ReleaseStatusIndicator';
import { RouteSimulationCell } from 'components/upcomingTreatments/RouteSimulationCell';
import { TreatmentIssuesIcon } from 'components/upcomingTreatments/TreatmentIssuesIcon';
import { UpcomingTreatmentRowMenu } from 'components/upcomingTreatments/UpcomingTreatmentRowMenu';
import { PRODUCT_GO_LIVE_DATE_PREFIX } from 'services/goLiveFilterHelpers';
import { AppRoutes } from 'services/routes';
import {
    useGetUpcomingTreatmentsMetadataQuery,
    useGetUpcomingTreatmentsQuery,
} from 'store/api/goLiveApi';
import {
    useGetAllSuppliersWithChildrenQuery,
    useGetPayerSegmentsQuery,
} from 'store/api/referenceApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const backToDestination = { route: AppRoutes.upcomingTreatments, text: 'Upcoming Treatments' };

const countFor = (filters, key, value) =>
    filters
        ?.find((filter) => filter.key === key)
        ?.values?.find((item) => String(item.value) === String(value))?.count ?? 0;

const mergeCounts = (filters, key, options) =>
    options.map((option) => ({
        ...option,
        count: countFor(filters, key, option.value),
    }));

const valuesFor = (filters, key) => filters?.find((filter) => filter.key === key)?.values;

const passCount = ({ value, text, count }) => ({
    value,
    text,
    count: count ?? 0,
});

const buildGoLiveDateFilterValues = (filters) => {
    const goLiveValues = filters?.find((f) => f.key === 'go_live_date')?.values ?? [];
    const expansionValues = filters?.find((f) => f.key === 'product_go_live_date')?.values ?? [];

    return [
        ...goLiveValues.map(({ value, count }) => ({
            value,
            text: formatDate(value, DateFormats.DATE),
            sortDate: value,
            isExpansion: false,
            count: count ?? 0,
        })),
        ...expansionValues.map(({ value, count }) => ({
            value: `${PRODUCT_GO_LIVE_DATE_PREFIX}${value}`,
            text: `${formatDate(value, DateFormats.DATE)} (Product Expansion)`,
            sortDate: value,
            isExpansion: true,
            count: count ?? 0,
        })),
    ]
        .sort((a, b) => {
            const byDate = sortDateAsc(a.sortDate, b.sortDate);
            if (byDate !== 0) return byDate;
            return Number(a.isExpansion) - Number(b.isExpansion);
        })
        .map(({ value, text, count }) => ({ value, text, count }));
};

const initialSortProperties = [{ id: 'patient_last_name', desc: false }];

const columns = ({ supplierMap }) => [
    {
        accessorKey: 'patient_last_name',
        header: 'Patient',
        size: 120,
        cell: ({ getValue, row }) => (
            <PrimaryData
                data={formatNameLastFirst(getValue(), row.original.patient_first_name)}
                customTooltipContent={
                    <>
                        <div>
                            {formatNameLastFirst(getValue(), row.original.patient_first_name)}
                        </div>
                        <div>DOB: {formatDate(row.original.patient_dob)}</div>
                        <div>Member ID: {row.original.member_id}</div>
                    </>
                }
            />
        ),
    },
    { accessorKey: 'current_segment_id', header: 'Payer Segment', size: 120 },
    {
        accessorKey: 'product_service_oid',
        header: 'Product OID',
        size: 200,
        cell: ({ getValue, row }) => (
            <ProductCell
                product={getValue()}
                isBrand={row.original.is_brand}
                isLdd={row.original.is_ldd}
            />
        ),
    },
    { accessorKey: 'incumbent_supplier', header: 'Incumbent Pharmacy', size: 100 },
    { accessorKey: 'prescriber_npi', header: 'Prescriber', size: 100 },
    {
        accessorKey: 'needs_by_date',
        header: 'Next Fill NBD',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'auth_expiration',
        header: 'Auth Expiration',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'issues',
        enableSorting: false,
        header: <Icon icon={IconNames.ISSUE} title="Issues" tooltipContent="Issues" />,
        size: 45,
        cell: ({ getValue }) => <TreatmentIssuesIcon issues={getValue()} />,
    },
    {
        accessorKey: 'preferred_pharmacy_offer',
        header: 'Preferred Pharmacy',
        size: 120,
        cell: ({ getValue, row }) => (
            <PreferredPharmacyOfferCell
                offer={getValue()}
                incumbentSupplierNpi={row.original.incumbent_supplier_npi}
                supplierMap={supplierMap}
            />
        ),
    },
    {
        accessorKey: 'route_name',
        header: 'Simulator Results',
        size: 120,
        cell: ({ getValue, row }) => (
            <RouteSimulationCell
                routeName={getValue()}
                simulationErrorMessage={row.original.simulation_error_message}
                simulationSupplierNpi={row.original.simulation_supplier_npi}
                simulationOfferRate={row.original.simulation_offer_rate}
                simulationReferentPriceType={row.original.simulation_referent_price_type}
                preBidSupplierNpi={row.original.pre_bid_supplier_npi}
                preBidRate={row.original.pre_bid_rate}
                preBidReferentPriceType={row.original.pre_bid_referent_price_type}
                backstopSupplierNpi={row.original.backstop_supplier_npi}
                backstopRate={row.original.backstop_rate}
                backstopReferentPriceType={row.original.backstop_referent_price_type}
                supplierMap={supplierMap}
            />
        ),
    },
    {
        accessorKey: 'release_status',
        header: 'Release Status',
        size: 100,
        cell: ({ getValue, row }) => (
            <ReleaseStatusIndicator
                status={getValue()}
                batchName={row.original.upcoming_treatment_batch_name}
            />
        ),
    },
    {
        accessorKey: 'treatment_id',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ getValue, row }) => (
            <UpcomingTreatmentRowMenu
                treatmentId={getValue()}
                payerId={row.original.payer_id}
                productServiceOid={row.original.product_service_oid}
                releaseStatus={row.original.release_status}
                preferredPharmacyOffer={row.original.preferred_pharmacy_offer}
                incumbentMapping={
                    row.original.incumbent_supplier
                        ? [
                              {
                                  incumbent_supplier_npi: row.original.incumbent_supplier_npi,
                                  treatment_count: 1,
                              },
                          ]
                        : []
                }
            />
        ),
    },
];

/** Build hierarchy of incumbent supplier parents for filter */
const buildIncumbentSupplierFilters = (filters, supplierMap) => {
    // Build map of parent suppliers to their children
    const parentSupplierFilterMap = {};
    // Build list of children that are not assigned to a parent supplier (should not happen)
    const orphanedChildren = [];
    filters
        ?.toSorted((a, b) => sortAsc(a.text, b.text))
        .forEach((filter) => {
            const parentSupplierNpi = supplierMap?.[filter.value]?.parentSupplierNpi;
            if (!parentSupplierNpi) {
                // Child does not map to a parent supplier (should not happen)
                orphanedChildren.push(filter);
            } else if (parentSupplierFilterMap[parentSupplierNpi]) {
                parentSupplierFilterMap[parentSupplierNpi].push(filter);
            } else {
                parentSupplierFilterMap[parentSupplierNpi] = [filter];
            }
        });

    const sortedFilters = [
        ...Object.entries(parentSupplierFilterMap).map(([parentSupplierNpi, children]) => ({
            text: `${supplierMap?.[parentSupplierNpi]?.name} (${parentSupplierNpi})`,
            children,
        })),
        ...orphanedChildren,
    ].toSorted((a, b) => sortAsc(a.text, b.text));

    return [
        { value: 'EXCLUDE_NULL_INCUMBENT_PHARMACY', text: 'Only show configured pharmacies' },
    ].concat(sortedFilters);
};

const buildFilterCategories = (filters, payerSegmentMap, supplierMap) => {
    const showCounts = (filters?.length ?? 0) > 0;
    const groupFilterCategory = buildGroupFilterCategory(filters, showCounts);

    return [
        {
            key: 'go_live_date',
            title: 'Go-Live Date',
            showCounts,
            customValues: buildGoLiveDateFilterValues(filters),
        },
        {
            key: 'current_segment_id',
            title: 'Payer Segment',
            showCounts,
            customValues: valuesFor(filters, 'current_segment_id')
                ?.map((item) => ({
                    ...passCount(item),
                    text: payerSegmentMap?.[item.value]?.name,
                }))
                .sort((a, b) => sortAsc(a.text, b.text)),
        },
        groupFilterCategory,
        {
            key: 'incumbent_supplier',
            title: 'Incumbent Pharmacy',
            // Agora-only "Only show configured pharmacies" has no backend count.
            // showCounts here would make Strata treat that checkbox as count 0 (disabled).
            customValues: buildIncumbentSupplierFilters(
                valuesFor(filters, 'incumbent_supplier'),
                supplierMap,
            ),
        },
        {
            key: 'product_service_oid',
            title: 'Product',
            showCounts,
            customValues: valuesFor(filters, 'product_service_oid')?.map(passCount),
        },
        {
            key: 'preferred_pharmacy_offer',
            title: 'Preferred Pharmacy',
            showCounts,
            customValues: valuesFor(filters, 'preferred_pharmacy_offer')
                ?.map((item) => ({
                    ...passCount(item),
                    text: `${supplierMap?.[item.value]?.name} (${item.value})`,
                }))
                .sort((a, b) => sortAsc(a.text, b.text)),
        },
        {
            key: 'therapeutic_category',
            title: 'Therapeutic Category',
            showCounts,
            customValues: valuesFor(filters, 'therapeutic_category')?.map(passCount),
        },
        {
            key: 'next_fill_nbd',
            title: 'Next Fill NBD',
            showCounts,
            customValues: mergeCounts(filters, 'next_fill_nbd', [
                {
                    value: 'WITHIN_15_DAYS_OF_GO_LIVE_DATE',
                    text: 'NBD within 15 days before or after go-live date',
                },
            ]),
        },
        {
            key: 'has_issues',
            title: 'Issues',
            showCounts,
            customValues: mergeCounts(filters, 'has_issues', [
                { value: 'NO_PA', text: 'Missing PA' },
                { value: 'NO_RECENT_EVIDENCE', text: 'No recent evidence' },
                {
                    value: 'UNMANAGED_MISSING_PAYER_PRODUCT_CONFIG',
                    text: 'Unmanaged: Missing payer product config',
                },
                {
                    value: 'UNMANAGED_MISSING_ELIGIBILITY_RECORD',
                    text: 'Unmanaged: Missing eligibility record',
                },
                { value: 'None', text: 'None' },
            ]),
        },
        {
            key: 'upcoming_treatment_batch_name',
            title: 'Batch',
            showCounts,
            customValues: valuesFor(filters, 'upcoming_treatment_batch_name')?.map(passCount),
        },
        {
            key: 'release_status',
            title: 'Release Status',
            showCounts,
            customValues: mergeCounts(filters, 'release_status', [
                { value: 'BATCHED', text: 'Batched' },
                { value: 'NEW_LOAD', text: 'New Load' },
                { value: 'RELEASED', text: 'Released' },
            ]),
        },
        {
            key: 'product_type',
            title: 'Product Type',
            showCounts,
            customValues: mergeCounts(filters, 'product_type', [
                { value: 'BRAND', text: 'Brand' },
                { value: 'GENERIC', text: 'Generic' },
            ]),
        },
        {
            key: 'ldd',
            title: 'Is LDD',
            showCounts,
            customValues: mergeCounts(filters, 'ldd', [{ value: true, text: 'Is LDD' }]),
        },
        {
            key: 'on_segment_drug_list',
            title: 'Segment Drug List',
            customValues: [{ value: true, text: 'Only show products on the segment drug list' }],
        },
        {
            key: 'treatment_status',
            title: 'Treatment status',
            customValues: [
                { value: 'MANAGED', text: 'Managed' },
                {
                    text: 'Unmanaged',
                    children: [
                        { value: 'UNMANAGED', text: 'Unmanaged' },
                        { value: 'TEST_CLAIM', text: 'Test claim' },
                    ],
                },
            ],
        },
    ];
};

const DEFAULT_TREATMENT_STATUS = ['MANAGED'];

// eslint-disable-next-line complexity
export const UpcomingTreatmentsListPage = () => {
    const navigate = useNavigate();

    const {
        activeFilters,
        filterPaneWidth,
        search,
        sort,
        page,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
        updateCurrentPage,
    } = useGridState(Grids.UPCOMING_TREATMENTS, initialSortProperties);

    // The search box updates `search` on every keystroke to keep the input responsive,
    // but the upcoming-treatments query is expensive, so only feed the API a debounced
    // copy — otherwise each keystroke fires a concurrent query and saturates the DB.
    const debounceSearchText = useDebouncedFunction();
    const [debouncedSearchText, setDebouncedSearchText] = useState(search);

    const goLiveDateFilters = activeFilters?.go_live_date ?? [];
    const resolvedActiveFilters = useMemo(
        () => ({
            ...activeFilters,
            treatment_status: activeFilters?.treatment_status ?? DEFAULT_TREATMENT_STATUS,
        }),
        [activeFilters],
    );

    const filterQueryParams = {
        searchText: debouncedSearchText,
        goLiveDate: goLiveDateFilters.filter(
            (value) => !value.startsWith(PRODUCT_GO_LIVE_DATE_PREFIX),
        ),
        nextFillNbd: activeFilters?.next_fill_nbd,
        productServiceOid: activeFilters?.product_service_oid,
        segmentId: activeFilters?.current_segment_id,
        incumbentSupplier: activeFilters?.incumbent_supplier,
        productType: activeFilters?.product_type,
        isLDD: activeFilters?.ldd,
        hasIssues: activeFilters?.has_issues,
        releaseStatus: activeFilters?.release_status,
        upcomingTreatmentBatchName: activeFilters?.upcoming_treatment_batch_name,
        preferredPharmacyOffer: activeFilters?.preferred_pharmacy_offer,
        therapeuticCategory: activeFilters?.therapeutic_category,
        treatmentStatus: resolvedActiveFilters.treatment_status,
        onSegmentDrugList: activeFilters?.on_segment_drug_list,
        productGoLiveDate: goLiveDateFilters
            .filter((value) => value.startsWith(PRODUCT_GO_LIVE_DATE_PREFIX))
            .map((value) => value.slice(PRODUCT_GO_LIVE_DATE_PREFIX.length)),
    };
    filterQueryParams.groupId = groupIdFromActiveFilters(activeFilters);
    const apiQueryParams = {
        ...filterQueryParams,
        page,
        sort,
    };

    const { data: metadata, isFetching: isMetadataFetching } =
        useGetUpcomingTreatmentsMetadataQuery(filterQueryParams);

    const {
        data: currentPageData,
        isFetching: isCurrentPageDataFetching,
        refetch,
        fulfilledTimeStamp: lastUpdatedTimestamp,
    } = useGetUpcomingTreatmentsQuery(apiQueryParams);

    const { data: payerSegmentData } = useGetPayerSegmentsQuery();
    const { payerSegmentMap } = payerSegmentData ?? {};

    const { data: allSuppliersData } = useGetAllSuppliersWithChildrenQuery();
    const { supplierMap } = allSuppliersData ?? {};

    const gridData = currentPageData?.upcoming_treatments?.map((treatment) => ({
        ...treatment,
        current_segment_id: payerSegmentMap?.[treatment.current_segment_id]?.name,
        issues: JSON.parse(treatment.issues),
    }));

    const filterCategories = useMemo(
        () => buildFilterCategories(metadata?.filters, payerSegmentMap, supplierMap),
        [metadata?.filters, payerSegmentMap, supplierMap],
    );

    // #region Bulk Actions Menu

    const [showReleaseBatchDialog, setShowReleaseBatchDialog] = useState(false);
    const [showCreateBatchDialog, setShowCreateBatchDialog] = useState(false);
    const [showAddToBatchDialog, setShowAddToBatchDialog] = useState(false);
    const [showRemoveFromBatchDialog, setShowRemoveFromBatchDialog] = useState(false);
    const [showReprocessTreatmentsDialog, setShowReprocessTreatmentsDialog] = useState(false);
    const [showPreferredPharmacyOfferDialog, setShowPreferredPharmacyOfferDialog] = useState(false);
    const [showRemovePreferredPharmacyOfferDialog, setShowRemovePreferredPharmacyOfferDialog] =
        useState(false);
    const [showRunRouteSimulatorDialog, setShowRunRouteSimulatorDialog] = useState(false);

    const menuItems = [
        {
            text: AddToBatchDialog.Title,
            onItemClick: () => setShowAddToBatchDialog(true),
        },
        {
            text: CreateBatchDialog.Title,
            onItemClick: () => setShowCreateBatchDialog(true),
        },
        {
            text: RemoveFromBatchDialog.Title,
            onItemClick: () => setShowRemoveFromBatchDialog(true),
        },
        {
            text: PreferredPharmacyOfferDialog.Title,
            onItemClick: () => setShowPreferredPharmacyOfferDialog(true),
        },
        {
            text: RemovePreferredPharmacyOfferDialog.Title,
            onItemClick: () => setShowRemovePreferredPharmacyOfferDialog(true),
        },
        {
            text: RunRouteSimulatorDialog.Title,
            onItemClick: () => setShowRunRouteSimulatorDialog(true),
        },
        {
            text: ReprocessTreatmentDialog.Title,
            onItemClick: () => setShowReprocessTreatmentsDialog(true),
        },
    ];

    // #endregion

    return (
        <>
            <CreateBatchDialog
                isOpen={showCreateBatchDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowCreateBatchDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <AddToBatchDialog
                isOpen={showAddToBatchDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowAddToBatchDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <RemoveFromBatchDialog
                isOpen={showRemoveFromBatchDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowRemoveFromBatchDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <ReprocessTreatmentDialog
                isOpen={showReprocessTreatmentsDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowReprocessTreatmentsDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <PreferredPharmacyOfferDialog
                isOpen={showPreferredPharmacyOfferDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowPreferredPharmacyOfferDialog(false)}
                treatmentsCount={metadata?.count}
                incumbentMapping={metadata?.incumbentMapping}
                staggeredAllowed
            />
            <RemovePreferredPharmacyOfferDialog
                isOpen={showRemovePreferredPharmacyOfferDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowRemovePreferredPharmacyOfferDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <RunRouteSimulatorDialog
                isOpen={showRunRouteSimulatorDialog}
                apiQueryParams={apiQueryParams}
                onClose={() => setShowRunRouteSimulatorDialog(false)}
                treatmentsCount={metadata?.count}
            />
            <ReleaseBatchDialog
                isOpen={showReleaseBatchDialog}
                onClose={() => setShowReleaseBatchDialog(false)}
            />
            <FormPageContainer>
                <GridWithFilters
                    title="Upcoming Treatments"
                    activeFilters={resolvedActiveFilters}
                    columns={columns({ supplierMap })}
                    data={gridData}
                    totalCount={metadata?.count}
                    isLoading={
                        isMetadataFetching ||
                        isCurrentPageDataFetching ||
                        search !== debouncedSearchText
                    }
                    manualSorting
                    sortProperties={sort}
                    onSort={updateGridSort}
                    enableSearch
                    searchText={search}
                    onSearch={(value) => {
                        updateGridSearch(value);
                        debounceSearchText(() => setDebouncedSearchText(value));
                    }}
                    filterCategories={filterCategories}
                    onFilter={updateGridActiveFilters}
                    filterPaneRenderWidth={filterPaneWidth}
                    onFilterPaneResize={updateFilterPaneWidth}
                    currentPage={page}
                    onPageChange={updateCurrentPage}
                    onRowClick={(rowData) =>
                        navigate(
                            AppRoutes.treatmentDetails(rowData.payer_id, rowData.treatment_id),
                            {
                                state: { backToDestination },
                            },
                        )
                    }
                    topRightContent={
                        <div className="sds_referral-grid__refresh">
                            {lastUpdatedTimestamp && (
                                <span className="sds_referral-grid__refresh__timestamp">
                                    {`Last Updated ${formatDate(lastUpdatedTimestamp, DateFormats.DATE_TIME)}`}
                                </span>
                            )}
                            <ActionButtonText
                                label="Refresh"
                                leftIcon={IconNames.REFRESH}
                                onClick={refetch}
                            />
                            <Button
                                label="Release A Batch"
                                intent={Button.Intent.NONE}
                                onClick={() => setShowReleaseBatchDialog(true)}
                            />
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                label="Bulk Actions"
                            />
                        </div>
                    }
                />
            </FormPageContainer>
        </>
    );
};
